import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import MailOrderIcon from '@/components/customercenter-ui/icons/mailOrderImageIcon';
import {
  getContentByTitle,
  getAttribute,
} from '@/customercenter-ui/components/product/details/helpers/utils/productDataUtils';
import useMediaQuery from '@/frontastic/hooks/useMediaQuery';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { BadgeConfiguration, getOrderedMatchedBadges, ProductBadgingEntry } from '@/helpers/productBadging';
import { tablet } from '@/helpers/utils/screensizes';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import ProductRuleBadges from './ProductRuleBadges';
import ChevronLeft from '../../../../../../customercenter-ui/components/icons/chevronLeft';
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
} from '../../../../../../customercenter-ui/components/ui/carousel';
import ContentStackImage from '../../../../../common/contentStackImage/contentStackImage';
import { ProductContext } from '../../contexts';

const normalizeBadgePriorityKey = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-_]/g, '');

function isEnableBadgeConfigurations(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
}

const PdpCarouselImages = () => {
  const { initialSize, initialColor, product, isMailOrderProduct } = useContext(ProductContext);
  const getAttributeValue = useCallback(getAttribute, [initialSize]);
  const projectSettings = useProjSettings();
  const [stageMainImage, setStageMainImage] = useState<string>('');
  const [ruleBadges, setRuleBadges] = useState<BadgeConfiguration[]>([]);
  const [activeAltImage, setActiveAltImage] = useState<number>(0);
  const [altImages, setAltImages] = useState<[]>([]);
  const [api, setApi] = useState<CarouselApi>();
  const selectedDivision = getSelectedDivision() ?? 'ZB';
  const [isTabletAndUpSize] = useMediaQuery(tablet);
  const [cmsProductBadgingEntries, setCmsProductBadgingEntries] = useState<ProductBadgingEntry[] | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const badgeRulesTitle = 'Product Badging';

  const handleAltImageClick = (url: string, index: number) => {
    setStageMainImage(url.replace(/width\=[0-9]+/g, 'width=750'));
    setActiveAltImage(index);
    if (api && !api.slidesInView().includes(index)) api.scrollTo(index);
  };

  const handleLeftArrowClick = () => {
    if (activeAltImage > 0) handleAltImageClick(altImages[activeAltImage - 1] || '', activeAltImage - 1);
  };
  const handleRightArrowClick = () => {
    if (activeAltImage + 1 < altImages.length)
      handleAltImageClick(altImages[activeAltImage + 1] || '', activeAltImage + 1);
  };

  useEffect(() => {
    setStageMainImage(getAttributeValue('mainImageURL', initialSize));
    setAltImages(getAttributeValue('altImageURLs', initialSize));
    setActiveAltImage(0);
    api?.scrollTo(0);
  }, [initialSize]);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };
    update();
    api.on('select', update);
    api.on('reInit', update); // runs when altImages change / slides re-mount
    return () => {
      api.off('select', update);
      api.off('reInit', update);
    };
  }, [api]);

  const badgeSettings = projectSettings?.badgeConfigurations.find(
    (badge: any) => badge?.['SITE_ID'] === selectedDivision,
  )?.BADGES_LIST?.[0];

  const maxBadgesPdpResolved = useMemo(() => {
    const raw = badgeSettings?.MAX_BADGES_PDP;
    const n = typeof raw === 'string' ? parseInt(String(raw).trim(), 10) : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [badgeSettings?.MAX_BADGES_PDP]);

  const pdpBadgeConfigOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    const rows = Array.isArray(badgeSettings?.BADGE_CONFIGURATIONS) ? badgeSettings?.BADGE_CONFIGURATIONS : [];
    rows.forEach((row: any, index: number) => {
      const key = normalizeBadgePriorityKey(row?.badgeKey);
      if (key && !map.has(key)) map.set(key, index);
    });
    return map;
  }, [badgeSettings?.BADGE_CONFIGURATIONS]);

  const mergedPdpBadgeSelection = useMemo(() => {
    const max = typeof maxBadgesPdpResolved === 'number' && maxBadgesPdpResolved > 0 ? maxBadgesPdpResolved : Infinity;
    const orderedCandidates: Array<{
      source: 'cms' | 'legacy';
      key: string;
      order: number;
      cmsBadge?: BadgeConfiguration;
      legacyLabel?: string;
      seq: number;
    }> = [];

    (Array.isArray(ruleBadges) ? ruleBadges : []).forEach((entry, index) => {
      const key = normalizeBadgePriorityKey(entry?.badgeKey);
      if (!key) return;
      orderedCandidates.push({
        source: 'cms',
        key,
        order: pdpBadgeConfigOrderMap.get(key) ?? Number.MAX_SAFE_INTEGER,
        cmsBadge: entry,
        seq: index,
      });
    });

    const orderedAll = orderedCandidates.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.source !== b.source) return a.source === 'cms' ? -1 : 1;
      return a.seq - b.seq;
    });

    const seenKeys = new Set<string>();
    const cmsBadges: BadgeConfiguration[] = [];
    const legacyLabels: string[] = [];
    for (const candidate of orderedAll) {
      if (seenKeys.has(candidate.key)) continue;
      if (cmsBadges.length + legacyLabels.length >= max) break;
      seenKeys.add(candidate.key);
      if (candidate.source === 'cms' && candidate.cmsBadge) cmsBadges.push(candidate.cmsBadge);
      if (candidate.source === 'legacy' && candidate.legacyLabel) legacyLabels.push(candidate.legacyLabel);
    }
    return { cmsBadges, legacyLabels };
  }, [ruleBadges, maxBadgesPdpResolved, pdpBadgeConfigOrderMap]);

  const pdpVisibleRuleBadges = mergedPdpBadgeSelection.cmsBadges;

  const cmsBadgesEnabled = useMemo(
    () => isEnableBadgeConfigurations(badgeSettings?.ENABLE_BADGE_CONFIGURATIONS),
    [badgeSettings?.ENABLE_BADGE_CONFIGURATIONS],
  );

  useEffect(() => {
    if (!cmsBadgesEnabled) {
      setCmsProductBadgingEntries(null);
      setRuleBadges([]);
      return;
    }

    let cancelled = false;
    const fetchBadgeRulesOnce = async () => {
      const badgeRules: any = await getContentByTitle(badgeRulesTitle, true);

      if (cancelled) return;
      setCmsProductBadgingEntries(
        Array.isArray(badgeRules?.productBadgingEntries) ? badgeRules?.productBadgingEntries : [],
      );
    };
    fetchBadgeRulesOnce();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getContentByTitleOnly from useContent is unstable
  }, [cmsBadgesEnabled, badgeRulesTitle]);

  useEffect(() => {
    if (!cmsBadgesEnabled || cmsProductBadgingEntries === null) {
      setRuleBadges([]);
      return;
    }
    const orderedRuleBadges = getOrderedMatchedBadges({
      productBadgingEntries: cmsProductBadgingEntries,
      badgeConfigurations: badgeSettings?.BADGE_CONFIGURATIONS,
      productCategories: product?.categories,
      initialSizeKey: initialSize?.key,
      initialColorKey: initialColor?.key,
      attributesRaw: initialSize?.attributesRaw,
      maxVisibleBadges: maxBadgesPdpResolved,
    });
    setRuleBadges(orderedRuleBadges);
  }, [
    cmsBadgesEnabled,
    cmsProductBadgingEntries,
    badgeSettings?.ENABLE_BADGE_CONFIGURATIONS,
    product?.categories,
    initialSize?.key,
    initialColor?.key,
    initialSize?.attributesRaw,
    maxBadgesPdpResolved,
  ]);

  const handleZoomHover = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const img = container.querySelector('.zoom-img') as HTMLElement;

    if (!img) return;

    const rect = container.getBoundingClientRect();

    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    img.style.transformOrigin = `${x}% ${y}%`;
    img.style.transform = 'scale(2)';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const img = e.currentTarget.querySelector('.zoom-img') as HTMLElement;

    if (!img) return;

    img.style.transformOrigin = 'center center';
    img.style.transform = 'scale(1)';
  };

  return (
    <>
      {isMailOrderProduct ? (
        <MailOrderIcon />
      ) : (
        initialSize && (
          <section className="w-full">
            <div className="relative mb-4">
              {isTabletAndUpSize ? <ProductRuleBadges badges={pdpVisibleRuleBadges} layout="pdpCorner" /> : null}
              {altImages?.length > 1 && activeAltImage > 0 ? (
                <button
                  onClick={handleLeftArrowClick}
                  className={`absolute left-4 z-[2] rounded-full bg-[rgba(255,255,255,0.6)] shadow ${
                    isTabletAndUpSize
                      ? 'top-[calc(50%_-_27px)] h-[54px] w-[54px]'
                      : 'top-[calc(50%_-_22px)] h-[44px] w-[44px]'
                  }`}
                >
                  <ChevronLeft
                    className={`mx-auto ${isTabletAndUpSize ? 'h-[22px] w-[12px]' : 'h-[18px] w-[10px]'}`}
                  ></ChevronLeft>
                </button>
              ) : (
                <></>
              )}
              <div className="zoom-container" onMouseMove={handleZoomHover} onMouseLeave={handleMouseLeave}>
                <div className="zoom-img">
                  <ContentStackImage src={stageMainImage} imageHeight={478} imageWidth={478} alt="" />
                </div>
              </div>
              {altImages?.length > 1 && activeAltImage + 1 < altImages?.length ? (
                <button
                  onClick={handleRightArrowClick}
                  className={`absolute right-4 z-[2] rounded-full bg-[rgba(255,255,255,0.6)] shadow ${
                    isTabletAndUpSize
                      ? 'top-[calc(50%_-_27px)] h-[54px] w-[54px]'
                      : 'top-[calc(50%_-_22px)] h-[44px] w-[44px]'
                  }`}
                >
                  <ChevronLeft
                    className={`mx-auto rotate-180 ${isTabletAndUpSize ? 'h-[22px] w-[12px]' : 'h-[18px] w-[10px]'}`}
                  ></ChevronLeft>
                </button>
              ) : (
                <></>
              )}
            </div>
            <div className="relative px-11 text-center">
              {isTabletAndUpSize ? (
                <>
                  <Carousel setApi={setApi}>
                    <CarouselContent>
                      {altImages?.map((source: any, index: number) => {
                        return (
                          <CarouselItem key={index} className="basis-full md:basis-1/5">
                            <ContentStackImage
                              key={index}
                              onClick={() => {
                                handleAltImageClick(source, index);
                              }}
                              className={`border${
                                activeAltImage === index ? '-black' : '-stoneberry-inputBorder'
                              } mx-1.5 cursor-pointer border p-0.5`}
                              src={source}
                              imageHeight={72}
                              imageWidth={72}
                              alt=""
                            />
                          </CarouselItem>
                        );
                      })}
                    </CarouselContent>
                  </Carousel>
                  <button
                    disabled={!canScrollPrev}
                    onClick={() => {
                      api?.scrollPrev();
                    }}
                    className={`absolute left-0 top-[calc(50%_-_20px)] h-[40px] w-[40px] rounded-full bg-[rgba(255,255,255,0.6)] shadow ${
                      canScrollPrev ? '' : 'opacity-50'
                    }`}
                  >
                    <ChevronLeft className="mx-auto h-[17px] w-[9px]"></ChevronLeft>
                  </button>
                  <button
                    disabled={!canScrollNext}
                    onClick={() => {
                      api?.scrollNext();
                    }}
                    className={`absolute right-0 top-[calc(50%_-_20px)] h-[40px] w-[40px] rounded-full bg-[rgba(255,255,255,0.6)] shadow ${
                      canScrollNext ? '' : 'opacity-50'
                    }`}
                  >
                    <ChevronLeft className="mx-auto h-[17px] w-[9px] rotate-180"></ChevronLeft>
                  </button>
                </>
              ) : (
                <>
                  {altImages?.map((source: any, index: number) => {
                    return (
                      <button
                        key={index}
                        className={`mx-1 rounded-full ${
                          activeAltImage === index
                            ? 'h-[12px] w-[12px] bg-black'
                            : activeAltImage - 1 === index || activeAltImage + 1 === index
                            ? 'h-[10px] w-[10px] bg-stoneberry-inputBorder'
                            : 'h-[6px] w-[6px] bg-stoneberry-inputBorder'
                        }`}
                      ></button>
                    );
                  })}
                </>
              )}
            </div>
          </section>
        )
      )}
    </>
  );
};

export default PdpCarouselImages;
